"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
    Mail, Phone, MapPin, Send, Loader2, Facebook,
    Youtube, Linkedin, CheckCircle2, ArrowRight, Github,
    MessageSquare, ExternalLink, Music, Sparkles
} from "lucide-react";
import AntiSpamCaptcha from "@/components/AntiSpamCaptcha";

export default function ContactPage() {
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [profile, setProfile] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");
    const [captchaValid, setCaptchaValid] = useState(false);

    useEffect(() => {
        fetch("/api/profile").then(r => r.json()).then(setProfile).catch(() => { });
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true); setError(""); setSuccess(false);
        try {
            const res = await fetch("/api/contact", {
                method: "POST",
                body: JSON.stringify(form),
                headers: { "Content-Type": "application/json" }
            });
            if (res.ok) {
                setSuccess(true);
                setForm({ name: "", email: "", subject: "", message: "" });
            } else {
                const d = await res.json();
                setError(d.error || "Une erreur est survenue.");
            }
        } catch { setError("Erreur réseau. Veuillez réessayer."); }
        finally { setLoading(false); }
    };

    const socials = [
        { icon: <Facebook size={20} />, href: profile?.facebook, label: "Facebook" },
        { icon: <Youtube size={20} />, href: profile?.youtube, label: "Youtube" },
        { icon: <Linkedin size={20} />, href: profile?.linkedin, label: "Linkedin" },
        { icon: <Github size={20} />, href: profile?.github, label: "Github" },
        { icon: <MessageSquare size={20} />, href: profile?.whatsapp ? `https://wa.me/${profile.whatsapp.replace(/[^0-9]/g, "")}` : null, label: "WhatsApp" },
        { icon: <ExternalLink size={20} />, href: profile?.freelance, label: "Freelance" },
        { icon: <Music size={20} />, href: profile?.tiktok, label: "TikTok" },
    ].filter(s => s.href);

    return (
        <main style={{ paddingTop: "10rem", paddingBottom: "8rem", minHeight: "100vh" }}>
            <div className="container" style={{ maxWidth: "80rem" }}>
                {/* Header */}
                <header style={{ marginBottom: "6rem" }}>
                    <div className="badge badge-primary" style={{ marginBottom: "1.5rem" }}>
                        Collaboration & Partenariat
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-end", gap: "2rem" }}>
                        <div style={{ maxWidth: "45rem" }}>
                            <h1 style={{ fontSize: "clamp(3rem, 6vw, 4.5rem)", marginBottom: "1.5rem" }}>
                                Donnons Vie à vos <span className="text-gradient">Idées.</span>
                            </h1>
                            <p style={{ color: "var(--muted-foreground)", fontSize: "1.2rem", lineHeight: 1.7 }}>
                                Que ce soit pour un projet IoT complexe, une architecture logicielle ou une simple question, je suis à votre écoute pour transformer vos visions en solutions techniques concrètes.
                            </p>
                        </div>
                    </div>
                </header>

                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "4rem" }} className="contact-grid">
                    {/* Info Side */}
                    <aside style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
                        <div className="card" style={{ padding: "4rem", background: "var(--primary)", color: "white", position: "relative", overflow: "hidden", border: "none" }}>
                            <div style={{ position: "absolute", top: "-2rem", right: "-2rem", width: "12rem", height: "12rem", background: "rgba(255,255,255,0.1)", borderRadius: "50%", filter: "blur(40px)" }} />
                            <div style={{ position: "absolute", bottom: "-4rem", left: "-4rem", width: "16rem", height: "16rem", background: "rgba(0,0,0,0.1)", borderRadius: "50%", filter: "blur(60px)" }} />

                            <h2 style={{ fontSize: "2rem", marginBottom: "3rem", position: "relative" }}>Coordonnées.</h2>

                            <div style={{ display: "flex", flexDirection: "column", gap: "2.5rem", position: "relative" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
                                    <div style={{ width: 56, height: 56, background: "rgba(255,255,255,0.15)", borderRadius: "var(--radius-lg)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(10px)" }}>
                                        <Mail size={24} />
                                    </div>
                                    <div>
                                        <p style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em", opacity: 0.7, marginBottom: "0.25rem" }}>Email professionnel</p>
                                        <p style={{ fontSize: "1.1rem", fontWeight: 700 }}>{profile?.email || "contact@metalab.africa"}</p>
                                    </div>
                                </div>

                                <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
                                    <div style={{ width: 56, height: 56, background: "rgba(255,255,255,0.15)", borderRadius: "var(--radius-lg)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(10px)" }}>
                                        <Phone size={24} />
                                    </div>
                                    <div>
                                        <p style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em", opacity: 0.7, marginBottom: "0.25rem" }}>Téléphone / WhatsApp</p>
                                        <p style={{ fontSize: "1.1rem", fontWeight: 700 }}>{profile?.phone || "+229 XX XX XX XX"}</p>
                                    </div>
                                </div>

                                <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
                                    <div style={{ width: 56, height: 56, background: "rgba(255,255,255,0.15)", borderRadius: "var(--radius-lg)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(10px)" }}>
                                        <MapPin size={24} />
                                    </div>
                                    <div>
                                        <p style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em", opacity: 0.7, marginBottom: "0.25rem" }}>Localisation</p>
                                        <p style={{ fontSize: "1.1rem", fontWeight: 700 }}>{profile?.address || "Bénin, Afrique de l'Ouest"}</p>
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginTop: "5rem", borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: "3rem" }}>
                                <p style={{ fontSize: "0.8rem", textTransform: "uppercase", fontWeight: 800, marginBottom: "1.5rem", opacity: 0.8 }}>Présence Digitale</p>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
                                    {socials.map((s, i) => (
                                        <a key={i} href={s.href!} target="_blank" rel="noopener noreferrer"
                                            style={{ width: 48, height: 48, background: "rgba(255,255,255,0.15)", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(10px)", transition: "all 0.3s" }}
                                            className="social-link">
                                            {s.icon}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="card" style={{ padding: "2rem", display: "flex", alignItems: "center", gap: "1.5rem", background: "var(--muted)" }}>
                            <div style={{ width: 56, height: 56, background: "var(--primary-light)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Sparkles className="text-primary" size={24} />
                            </div>
                            <p style={{ fontSize: "0.95rem", color: "var(--foreground)", fontWeight: 700 }}>
                                Je m'engage à répondre à toutes les sollicitations professionnelles sous <span className="text-primary">48 heures</span>.
                            </p>
                        </div>
                    </aside>

                    {/* Form Side */}
                    <div className="card" style={{ padding: "4rem" }}>
                        {success ? (
                            <div style={{ textAlign: "center", padding: "4rem 0" }}>
                                <div style={{ width: 80, height: 80, background: "var(--primary-light)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 2rem" }}>
                                    <CheckCircle2 size={40} className="text-primary" />
                                </div>
                                <h2 style={{ fontSize: "2rem", marginBottom: "1rem" }}>Transmission Réussie.</h2>
                                <p style={{ color: "var(--muted-foreground)", marginBottom: "3rem", fontSize: "1.1rem" }}>
                                    Votre message a bien été reçu. Je reviendrai vers vous très prochainement.
                                </p>
                                <button onClick={() => setSuccess(false)} className="btn btn-primary">
                                    Envoyer un autre message <ArrowRight size={20} style={{ marginLeft: "0.75rem" }} />
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
                                <h2 style={{ fontSize: "1.8rem", marginBottom: "0.5rem" }}>Formulaire de Contact</h2>

                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "2rem" }}>
                                    <div className="form-group">
                                        <label className="form-label" style={{ marginBottom: "0.75rem", display: "block", fontSize: "0.85rem", fontWeight: 700, textTransform: "uppercase", color: "var(--muted-foreground)" }}>Nom complet</label>
                                        <input type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="form-input" style={{ width: "100%", padding: "1rem", background: "var(--background)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)" }} placeholder="Jean Dupont" />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label" style={{ marginBottom: "0.75rem", display: "block", fontSize: "0.85rem", fontWeight: 700, textTransform: "uppercase", color: "var(--muted-foreground)" }}>Adresse Email</label>
                                        <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="form-input" style={{ width: "100%", padding: "1rem", background: "var(--background)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)" }} placeholder="jean.dupont@entreprise.com" />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label" style={{ marginBottom: "0.75rem", display: "block", fontSize: "0.85rem", fontWeight: 700, textTransform: "uppercase", color: "var(--muted-foreground)" }}>Sujet de votre demande</label>
                                    <input type="text" required value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="form-input" style={{ width: "100%", padding: "1rem", background: "var(--background)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)" }} placeholder="Collaboration / Expertise IoT / Devis..." />
                                </div>

                                <div className="form-group">
                                    <label className="form-label" style={{ marginBottom: "0.75rem", display: "block", fontSize: "0.85rem", fontWeight: 700, textTransform: "uppercase", color: "var(--muted-foreground)" }}>Message détaillé</label>
                                    <textarea required rows={5} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="form-input" style={{ width: "100%", padding: "1rem", background: "var(--background)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", resize: "none" }} placeholder="Décrivez votre projet ou votre besoin technique ici..." />
                                </div>

                                <AntiSpamCaptcha onVerify={setCaptchaValid} />

                                {error && (
                                    <div style={{ padding: "1.25rem", background: "rgba(239,68,68,0.1)", borderRadius: "var(--radius-md)", color: "#ef4444", fontSize: "0.9rem", fontWeight: 700, border: "1px solid #ef4444" }}>
                                        {error}
                                    </div>
                                )}

                                <button type="submit" disabled={loading || !captchaValid} className="btn btn-primary" style={{ width: "100%", padding: "1.25rem", justifyContent: "center", fontSize: "1.1rem" }}>
                                    {loading ? (
                                        <><Loader2 size={24} className="animate-spin" /> ENVOI EN COURS...</>
                                    ) : (
                                        <>TRANSMETTRE LE MESSAGE <Send size={22} style={{ marginLeft: "0.75rem" }} /></>
                                    )}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </div>

            <style>{`
                @media(min-width: 1024px) {
                    .contact-grid { grid-template-columns: 4fr 7fr !important; }
                }
                .social-link:hover {
                    background: white !important;
                    color: var(--primary) !important;
                    transform: translateY(-4px);
                }
            `}</style>
        </main>
    );
}
